import { Button, Flex, Form, Typography } from 'antd';
import { useCallback, useContext, useMemo, useState } from 'react';

import { type ModelTag, useUpdateTagMutation } from '@app/api/Tags';
import { useTriggerTagsSelect } from '@app/components/Modals/BotDetailsModal/hooks/useTriggerTagsSelect';
import {
  EMPTY_TRIGGER_TAGS,
  addTagToTriggerTags,
  getTriggerTagsError,
  mapTagsToMultiSelectOptions,
} from '@app/components/Modals/BotDetailsModal/utils/triggerTagsGroups';
import { SUCCESS } from '@app/constants';
import { useUserPrint } from '@app/hooks';
import { AddTagsModal } from '@app/pages/Tags/components';
import { NotificationContext } from '@app/providers';
import { handleCatchError } from '@app/services';
import { MultiSelect } from '@app/shared/components';
import type {
  MultiSelectOption,
  MultiSelectValue,
} from '@app/shared/components/MultiSelect/types';
import type { NTags } from '@app/types';

import type { FormInstance } from 'antd/lib';

const { Text } = Typography;
interface TagsComponentProps {
  tags: NTags.Tag[];
  mainForm: FormInstance;
  getTagsHandler: () => Promise<void>;
}
const DEFAULT_ICON = 'fa-solid fa-tag';
const FORM_ITEM_NAME = 'tags';

interface TriggerTagsSelectProps {
  options: MultiSelectOption[];
  // value/onChange/onSelectionChange подставляет Form.Item
  value?: MultiSelectValue;
  onChange?: (value: MultiSelectValue) => void;
  onSelectionChange?: (value: MultiSelectValue) => void;
}

const VALIDATE_TRIGGER =
  'onSelectionChange' satisfies keyof TriggerTagsSelectProps;

const noop = () => undefined;

const TriggerTagsSelect = ({
  options,
  value = EMPTY_TRIGGER_TAGS,
  onChange = noop,
  onSelectionChange,
}: TriggerTagsSelectProps) => {
  const { tagsChangeHandler } = useTriggerTagsSelect({
    value,
    onChange,
    onSelectionChange,
  });

  return (
    <MultiSelect
      className="full-width"
      allowClear
      options={options}
      value={value}
      onChange={tagsChangeHandler}
      fixedInputLabel="Теги"
      placeholder="Выберите теги"
      dataTestId="trigger-tags-select"
    />
  );
};

export const TagsComponent = ({
  tags,
  mainForm,
  getTagsHandler,
}: TagsComponentProps) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIcon, setSelectedIcon] = useState(DEFAULT_ICON);

  const [addTagForm] = Form.useForm();

  const { activeBranchId, userId, database } = useUserPrint();

  const { showNotification } = useContext(NotificationContext);
  const [updateTag] = useUpdateTagMutation();

  const tagOptions = useMemo(() => mapTagsToMultiSelectOptions(tags), [tags]);

  const openCreateTagModal = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleCancel = useCallback(() => {
    setIsModalOpen(false);
    addTagForm.resetFields();
    setSelectedIcon(DEFAULT_ICON);
  }, [addTagForm]);

  const submitMainForm = useCallback(async () => {
    const values = addTagForm.getFieldsValue();
    const tagModel: ModelTag = {
      Id: 0,
      Name: values.Name,
      Color:
        typeof values.Color === 'string'
          ? values.Color
          : values.Color?.metaColor
          ? values.Color.metaColor.toHexString()
          : '#1677FF',
      IconId: selectedIcon,
      Description: values.Description,
      IsDeleted: false,
      IsSelected: false,
      IsSystem: false,
      SystemId: null,
    };

    try {
      const { Id } = await updateTag({
        ActiveBranchId: activeBranchId,
        data: tagModel,
        logs: { isLogged: true, userId, database },
      }).unwrap();

      setIsModalOpen(false);
      showNotification('Тег обновлен успешно', SUCCESS);
      setSelectedIcon('fa-solid fa-tag');
      addTagForm.resetFields();

      await getTagsHandler();

      const tagsValue: MultiSelectValue | undefined =
        mainForm.getFieldValue(FORM_ITEM_NAME);
      mainForm.setFieldValue(
        FORM_ITEM_NAME,
        addTagToTriggerTags(tagsValue, Id)
      );
    } catch (error) {
      showNotification(handleCatchError(error));
    }
  }, [
    addTagForm,
    selectedIcon,
    updateTag,
    activeBranchId,
    userId,
    database,
    showNotification,
    getTagsHandler,
    mainForm,
  ]);

  return (
    <div className="item-container">
      <Flex justify="space-between" align="center">
        <Text strong>Теги триггера</Text>
        <Button type="link" size="small" onClick={openCreateTagModal}>
          Добавить тег
        </Button>
      </Flex>
      <Form.Item
        name={FORM_ITEM_NAME}
        validateTrigger={VALIDATE_TRIGGER}
        rules={[
          {
            validator: (_, value: MultiSelectValue | undefined) => {
              const error = getTriggerTagsError(value);

              return error
                ? Promise.reject(new Error(error))
                : Promise.resolve();
            },
          },
        ]}
      >
        <TriggerTagsSelect options={tagOptions} />
      </Form.Item>
      <AddTagsModal
        currentTagId={0}
        isModalOpen={isModalOpen}
        handleCancel={handleCancel}
        form={addTagForm}
        submitMainForm={submitMainForm}
        setSelectedIcon={setSelectedIcon}
        selectedIcon={selectedIcon}
      />
    </div>
  );
};
